import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Avatar,
  FormControlLabel,
  IconButton,
  Radio,
  Button,
  FormHelperText,
  RadioGroup,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import MultiSelect from "../common/MultiSelect";
import { useDispatch, useSelector } from "react-redux";
import { getMasterData, getProfileDetails, logoutClear, patchProfilePictureInStore, updateUserProfile, uploadImage } from "../../redux/actions/appActions";
import { SET_CITIES, SET_LANGUAGES } from "../../redux/actions/actionTypes";
import { getKeyValuePairs } from "../../redux/services/utils";
import moment from "moment";
import { MONTHS } from "../../constants/AppVarConstants";
import { DeleteOutline } from "@mui/icons-material";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { ROUTE_COMPLETE_PROFILE, ROUTE_LOOKING_FOR, ROUTE_MAIN } from "../../constants/routes";
import { RootState } from "../../redux/types/reduxState";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const UploadButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "15px 64px",
  fontSize: "16px",
  fontWeight: "600",
  borderRadius: "10px",
  border: "1px dashed #756DE9",
  backgroundColor: "#f5f5f5",
  color: "#756DE9",
  textTransform: "none",
  [theme.breakpoints.down("sm")]: {
    padding: "15px 40px",
  },
  cursor: "pointer",
}));

const GenderOption = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
}));

const startYear = moment().year() - 60; // Adjust the range as needed
const endYear = moment().year();
const currentYear = moment().year();

type FormValues = Yup.InferType<any>;

const schema = Yup.object().shape({
  gender: Yup.string().required("Gender is required"),
  languagesKnown: Yup.array().min(1, "Please select at least one language"),
  location: Yup.mixed()
  .test("is-required", "Please enter or select a location", (value: any) => {
    return value !== null && value !== undefined && typeof value === "object" && value.label && value.value;
  }),
  date: Yup.number()
    .typeError("Please select a valid date")
    .required("Please select a valid date")
    .test("is-future-date", "Date of birth cannot be in the future", function (value) {
      const selectedDate = value || 0;
      const selectedMonth = this.parent.month || 1;
      const selectedYear = this.parent.year || currentYear;

      const currentDate = moment().date();
      const currentMonth = moment().month() + 1; // Month is zero-based

      if (
        selectedYear > currentYear ||
        (selectedYear === currentYear && selectedMonth > currentMonth) ||
        (selectedYear === currentYear && selectedMonth === currentMonth && selectedDate > currentDate)
      ) {
        return false; // Validation fails if the date is in the future
      }

      return true;
    }),
  month: Yup.number().typeError("Please select a valid month").required("Please select a valid month"),
  year: Yup.number().typeError("Please select a valid year").required("Please select a valid year"),
});

const defaultValues = {
  gender: "",
  languagesKnown: [],
  location: null,
  date: "",
  month: "",
  year: "",
};

const resolveCityValue = (location: unknown): string => {
  if (!location) return "";
  if (typeof location === "string") return location.trim();
  if (typeof location === "object" && location !== null) {
    const obj = location as { value?: string; label?: string };
    return String(obj.value ?? obj.label ?? "").trim();
  }
  return "";
};

const UploadPicture = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profilePic, setProfilePic] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null) as any;

  const user = useSelector((state: any) => state.app.user);
  const userProfile = useSelector((state: RootState) => state.app.userProfile);
  const { languages: newLanguages, city } = userProfile || {};
  const { dob, gender } = user?.user || {};
  const { languages, cities } = useSelector((state: any) => state.app);

  const Logout = () => {
    dispatch(logoutClear(()=>{
        navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    dispatch(getMasterData("LANGUAGES", SET_LANGUAGES));
    dispatch(getMasterData("CITY", SET_CITIES));

    if (location.search.includes("edit")) {
      dispatch(getProfileDetails());
    }
  }, []);

  useEffect(() => {
    if (userProfile && Object.keys(userProfile).length > 0) {
      const date = dob?.split("-");
      setValue("languagesKnown", getKeyValuePairs(newLanguages));
      setValue("gender", gender);
      if (city && city.trim()) {
        setValue("location", { label: city, value: city });
      }
      setValue("date", date?.[2]);
      setValue("month", date?.[1]);
      setValue("year", date?.[0]);
    }
  }, [user, userProfile]);

  const onSubmit = async (data: FormValues) => {
    if (profilePic) {
      const res: any = await new Promise((resolve) => {
        dispatch(
          uploadImage(profilePic, (result) => {
            resolve(result);
          })
        );
      });

      const pictureUrl = res?.result;
      if (pictureUrl) {
        dispatch(patchProfilePictureInStore(pictureUrl));
      }

      const payload = {
        profilePicturePath: pictureUrl,
        dob: `${data?.year}-${data?.month}-${data?.date}`,
        gender: data?.gender,
        languages: data?.languagesKnown?.map((el: { value: string }) => el?.value),
        city: resolveCityValue(data?.location),
      };
      dispatch(
        updateUserProfile(payload, () =>
          navigate(location?.search?.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_LOOKING_FOR)
        )
      );
    } else {
      const payload = {
        dob: `${data?.year}-${data?.month}-${data?.date}`,
        gender: data?.gender,
        languages: data?.languagesKnown?.map((el: { value: string }) => el?.value),
        city: resolveCityValue(data?.location),
      };
      dispatch(
        updateUserProfile(payload, () =>
          navigate(location?.search?.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_LOOKING_FOR)
        )
      );
    }
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files[0];
    setProfilePic(file);
    setSelectedImage(URL.createObjectURL(file));
    event.target.value = null;
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#ffffff" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <HomeIcon  />
            </InputAdornment> 
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <LogoutIcon  />
            </InputAdornment> 
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            backgroundColor: "#eee",
            paddingTop: "20px",
            paddingBottom: "20px",
            px: 2,
          }}
        >
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <Box
            sx={{
              fontSize: "16px",
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: "400",
              color: "#1F2235",
              lineHeight: "1.5",
              p: 1,
              ml: 1,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif !important',
                // fontSize: "14px !important",
                fontWeight: "medium !important",
                color: "#1F2235",
              }}
            >
              1/2
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif !important',
                // fontSize: "14px !important",
                fontWeight: "medium !important",
                color: "#1F2235",
              }}
            >
              We would like to know more about you.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        p={2}
        bgcolor="#ffffff"
      >
        <Box position="relative" textAlign="left">
          {selectedImage && (
            <>
              <img
                src={selectedImage}
                alt="Uploaded Profile Pic"
                style={{ width: "100px", height: "100px", borderRadius: "50%" }}
                key={selectedImage}
              />
              <IconButton
                onClick={() => document.getElementById("upload-input")?.click()}
                sx={{
                  position: "absolute",
                  top: 60,
                  left: 70,
                  bgcolor: "#756DE9 !important",
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  padding: "8px",
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={handleRemoveImage}
                sx={{
                  position: "absolute",
                  top: 60,
                  left: 120,
                  zIndex: 1, // Ensure remove icon is above the image
                  bgcolor: "#e2e2e2",
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  border: "1px solid red",
                }}
              >
                <DeleteOutline sx={{ color: "red" }} />
              </IconButton>
            </>
          )}
        </Box>
        {!selectedImage && (
          <UploadButton onClick={() => document.getElementById("upload-input")?.click()}>
            <img src="images/upload-line.png" width="20px" style={{ marginRight: "10px" }}></img> Upload Profile Pic
          </UploadButton>
        )}
        <input type="file" id="upload-input" accept="image/*" style={{ display: "none" }} onChange={handleFileUpload} />
        <Typography mt={2}>Date Of Birth</Typography>
        <Box className="mt-4">
          <Stack
            flexWrap="wrap"
            direction={{ xs: "row", sm: "row" }}
            spacing={{ xs: 1, sm: 2, md: 4 }}
            className="dob-box"
          >
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <FormControl className="social-m">
                  <InputLabel id="demo-simple-select-label">DD</InputLabel>
                  <Select
                    className="me-0 dob-btn"
                    sx={{
                      fontSize: "16px",
                      whiteSpace: "nowrap",
                      boxShadow: "inset 0px 1px 3px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "rgba(75, 68, 212, 0.04)",
                      borderRadius: "95px",
                      color: "#6e6e6e",
                      "& fieldset": {
                        border: "none",
                      },
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={field.value}
                    name="date"
                    label="date"
                    onChange={field.onChange}
                  >
                    {Array.from({ length: 31 }).map((_, index) => {
                      const day = index + 1;
                      // Allow selection of current and past days for the current month and year
                      if (
                        currentYear === watch("year") &&
                        watch("month") === moment().month() + 1 &&
                        day > moment().date()
                      ) {
                        return null;
                      }
                      const formattedDay = day < 10 ? `0${day}` : `${day}`;

                      return (
                        <MenuItem key={formattedDay} value={formattedDay}>
                          {formattedDay}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="month"
              render={({ field }) => (
                <FormControl className="social-m">
                  <InputLabel id="demo-simple-select-label">MM</InputLabel>
                  <Select
                    className="me-0 dob-btn"
                    sx={{
                      borderRadius: "99px",
                      boxShadow: "inset 0 0 11px 2px #ebe8e8",
                      "& fieldset": {
                        border: "none",
                      },
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={field.value}
                    name="month"
                    label="month"
                    onChange={field.onChange}
                  >
                    {Array.from({ length: 12 }).map((_, index) => {
                      const month = index + 1;
                      const formattedMonth = month < 10 ? `0${month}` : `${month}`;

                      return (
                        <MenuItem key={formattedMonth} value={formattedMonth}>
                          {MONTHS?.[index]}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="year"
              render={({ field }) => (
                <FormControl className="social-m">
                  <InputLabel id="demo-simple-select-label">YYYY</InputLabel>
                  <Select
                    className="me-0 dob-btn"
                    sx={{
                      borderRadius: "99px",
                      boxShadow: "inset 0 0 11px 2px #ebe8e8",
                      "& fieldset": {
                        border: "none",
                      },
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={field.value}
                    name="year"
                    label="year"
                    onChange={field.onChange}
                  >
                    {Array.from({ length: endYear - startYear + 1 }).map((_, index) => {
                      const year = startYear + index;
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2, display: "block" }}>
            {errors?.date && errors?.date?.type === "is-future-date"
              ? `${errors?.date?.message}`
              : errors?.date || errors?.month || errors?.year
              ? "Date of birth is required"
              : " "}
          </FormHelperText>
        </Box>

        <Box mt={2}>
          <Typography>Your Gender</Typography>
          <Controller
            control={control}
            name="gender"
            render={({ field, fieldState: { error } }) => (
              <Box>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                >
                  <FormControlLabel
                    value="Male"
                    control={<Radio />}
                    label={
                      <GenderOption>
                        <Typography>Male</Typography>
                      </GenderOption>
                    }
                  />
                  <FormControlLabel
                    value="Female"
                    control={<Radio />}
                    label={
                      <GenderOption>
                        <Typography>Female</Typography>
                      </GenderOption>
                    }
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label={
                      <GenderOption>
                        <Typography>Other</Typography>
                      </GenderOption>
                    }
                  />
                </RadioGroup>
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>{error ? error?.message : " "}</FormHelperText>
              </Box>
            )}
          />
        </Box>

        <Typography mt={2}>Language Known</Typography>
        <MultiSelect name="languagesKnown" control={control} options={getKeyValuePairs(languages)} />

        <Box>
          <Typography mt={2}>Current Location</Typography>
        </Box>

        <MultiSelect
          multiple={false}
          name="location"
          control={control}
          options={getKeyValuePairs(cities)}
          freesolo
        />
        {location?.search?.includes("edit") ? (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              justifyContent: "center",
              alignItems: "center",
              padding: "15px 64px",
              marginTop: "20px",
              marginBottom: "15px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              backgroundColor: "#756DE9",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "95px",
              textTransform: "none",
            }}
          >
            Save
          </Button>
        ) : (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              justifyContent: "center",
              alignItems: "center",
              padding: "15px 64px",
              marginTop: "20px",
              marginBottom: "15px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              backgroundColor: "#756DE9",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "95px",
              textTransform: "none",
            }}
          >
            Next Up: Preference
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UploadPicture;
